import { useState, useEffect } from "react";
import { toast } from "sonner";
import { BadgePercent, Check, Loader2, X, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useVeteranDiscount, getVerifiedDiscount } from "@/lib/veteran";
import { VeteranVerifyModal } from "@/components/VeteranVerifyModal";

// Reusable "Have a discount code?" field. Validates against the backend and
// reports the applied code + percent to the parent so it can pass it to checkout.
// When the owner requires verification, it offers a "Verify your service" step
// that issues a personal code and applies it automatically.
export function CheckoutPromo({ kind, applied, onApply, onClear }) {
  const cfg = useVeteranDiscount();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [verify, setVerify] = useState(false);

  // Auto-apply a previously verified personal code (validate against this kind).
  useEffect(() => {
    if (applied) return;
    const v = getVerifiedDiscount();
    if (v?.code) validate(v.code, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = async (c, silent = false) => {
    const val = (c || "").trim();
    if (!val) return;
    setBusy(true);
    try {
      const { data } = await api.post("/discount/validate", { code: val, kind });
      if (data.valid) {
        onApply?.({ code: data.code, percent: data.percent });
        if (!silent) toast.success(`Code applied — ${data.percent}% off.`);
        setOpen(false);
      } else if (!silent) {
        toast.error(data.message || "That code isn't valid.");
      }
    } catch (e) {
      if (!silent) toast.error("Couldn't check that code — please try again.");
    } finally { setBusy(false); }
  };

  if (applied) {
    return (
      <div className="flex items-center justify-between gap-2 border border-gold/40 bg-gold/10 px-3 py-2 text-sm" data-testid="promo-applied">
        <span className="inline-flex items-center gap-2 text-gold font-medium">
          <BadgePercent size={15} /> {applied.code} — {applied.percent}% off applied
        </span>
        <button onClick={() => onClear?.()} data-testid="promo-clear" className="text-gray-400 hover:text-white" aria-label="Remove code"><X size={15} /></button>
      </div>
    );
  }

  const requireVerify = cfg?.require_verification;

  return (
    <div className="space-y-2">
      {requireVerify && (
        <button onClick={() => setVerify(true)} data-testid="promo-verify"
          className="inline-flex items-center gap-1.5 text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 transition-colors">
          <ShieldCheck size={13} /> Verify your service to unlock your discount
        </button>
      )}
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="promo-open"
          className="block text-xs text-gold hover:text-gold-light underline underline-offset-2">
          {requireVerify ? "Already have a code?" : "Have a veteran / discount code?"}
        </button>
      ) : (
        <div className="flex items-center gap-2" data-testid="promo-field">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code"
            data-testid="promo-input" onKeyDown={(e) => e.key === "Enter" && validate(code)}
            className="flex-1 bg-navy border border-white/20 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none uppercase" />
          <button onClick={() => validate(code)} disabled={busy} data-testid="promo-apply"
            className="inline-flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Apply
          </button>
        </div>
      )}
      {verify && (
        <VeteranVerifyModal percent={cfg?.percent} onVerified={(d) => validate(d.code)} onClose={() => setVerify(false)} />
      )}
    </div>
  );
}
